import React from "react";

import { hydrateRoot, createRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { getThemeStyles } from "@atlaskit/tokens";
import { MeResponse } from "@atlassiansox/asc-stargate";

import {
    IntercomSettingsProperties,
    IntercomProperties
} from "@/libraries/intercom/src";
import { ConfirmationPageOverlayVariants } from "@/templates/confirmation-page/src/hooks";

import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import {
    AvailableProductsResponse,
    GetSignupUserTraitsResponse
} from "./libraries/bxp-web-stargate-client/src";
import { PageWrapper } from "./page-wrapper";
import { SentryIntlProvider } from "./utils/sentry-intl";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    type FeatureFlagType =
        | "boolean"
        | "string"
        | "number"
        | ConfirmationPageOverlayVariants;

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AvailableProductsResponse;
        signupUserTraits?: GetSignupUserTraitsResponse;
        userProfile?: MeResponse;
        templates?: Template[];
        collections?: TemplateCollection[];
        themeStyles: Awaited<ReturnType<typeof getThemeStyles>>;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
        Intercom?: IntercomProperties;
        intercomSettings?: IntercomSettingsProperties;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    const { locale } = parseRoute(window.location);

    initializeFeatureGatesClient(window.featureGateBootstrapValues).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                >
                    <PageWrapper
                        pureClientMode={true}
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        signupUserTraits={window.signupUserTraits}
                        userProfile={window.userProfile}
                        countryCode={window.countryCode}
                        templates={window.templates}
                        collections={window.collections}
                    >
                        {Component}
                    </PageWrapper>
                </SentryIntlProvider>
            );
        } else {
            hydrateRoot(
                // @ts-ignore
                document.getElementById("wac-root"),
                <SentryIntlProvider
                    locale={locale}
                    messages={window.SSR_languagePack}
                >
                    <Layout
                        location={window.location}
                        pricing={window.prices}
                        availableProducts={window.availableProducts}
                        signupUserTraits={window.signupUserTraits}
                        templates={window.templates}
                        collections={window.collections}
                        userProfile={window.userProfile}
                        countryCode={window.countryCode}
                        themeStyles={window.themeStyles}
                    >
                        {Component}
                    </Layout>
                </SentryIntlProvider>
            );
        }
    });
};
